import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  getEmployeeWorkMode,
  setEmployeeWorkMode,
  deleteEmployeeWorkMode,
  type WorkModeRange,
} from "../../api/employees";

const MODES = [
  { value: "in-office", label: "In Office", short: "In Office" },
  { value: "remote", label: "Remote (WFH)", short: "Remote" },
  { value: "hybrid", label: "Hybrid", short: "Hybrid" },
];

export const workModeLabel = (m?: string | null) =>
  MODES.find((x) => x.value === (m || "").toLowerCase())?.label || (m || "—");

// Compact form for the employee-card chip, which shares a row with the Active
// badge — the full "Remote (WFH)" was wide enough to wrap onto its own line.
export const workModeShortLabel = (m?: string | null) =>
  MODES.find((x) => x.value === (m || "").toLowerCase())?.short || (m || "—");

export default function WorkModeDialog({
  employeeId,
  employeeName,
  open,
  onOpenChange,
  onChanged,
}: {
  employeeId: string | null;
  employeeName?: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onChanged?: (mode: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [currentMode, setCurrentMode] = useState<string>("");
  const [defaultDraft, setDefaultDraft] = useState<string>("in-office");
  const [savingDefault, setSavingDefault] = useState(false);
  const [ranges, setRanges] = useState<WorkModeRange[]>([]);

  // Date-range override form
  const [ovMode, setOvMode] = useState("remote");
  const [ovFrom, setOvFrom] = useState("");
  const [ovTo, setOvTo] = useState("");
  const [addingOv, setAddingOv] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const info = await getEmployeeWorkMode(employeeId);
      setCurrentMode(info.current_mode || "");
      setDefaultDraft(info.current_mode || "in-office");
      setRanges(info.ranges || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't load work mode.");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const saveDefault = async () => {
    if (!employeeId) return;
    setSavingDefault(true);
    try {
      await setEmployeeWorkMode(employeeId, { mode: defaultDraft });
      setCurrentMode(defaultDraft);
      onChanged?.(defaultDraft);
      toast.success("Default work mode updated.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingDefault(false);
    }
  };

  const addOverride = async () => {
    if (!employeeId) return;
    if (!ovFrom) {
      toast.error("Pick a start date for the range.");
      return;
    }
    if (ovTo && ovTo < ovFrom) {
      toast.error("End date must be on/after the start date.");
      return;
    }
    setAddingOv(true);
    try {
      await setEmployeeWorkMode(employeeId, {
        mode: ovMode,
        from_date: ovFrom,
        to_date: ovTo || undefined,
      });
      toast.success("Date-range mode added.");
      setOvFrom("");
      setOvTo("");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't add range.");
    } finally {
      setAddingOv(false);
    }
  };

  const removeRange = async (id: string) => {
    try {
      await deleteEmployeeWorkMode(id);
      setRanges((rs) => rs.filter((r) => r.id !== id));
      toast.success("Range removed.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    }
  };

  const selectCls =
    "text-sm rounded-lg border border-gray-200 bg-white px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200";
  const dateCls =
    "text-sm rounded-lg border border-gray-200 bg-white px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-base font-bold text-gray-900">
            Work mode{employeeName ? ` — ${employeeName}` : ""}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Default mode */}
            <section>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Default mode
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={defaultDraft}
                  onChange={(e) => setDefaultDraft(e.target.value)}
                  className={`${selectCls} flex-1`}
                >
                  {MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={saveDefault}
                  disabled={savingDefault || defaultDraft === currentMode}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {savingDefault && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Set default
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Current: {workModeLabel(currentMode)} — this applies to any day
                not covered by a range below.
              </p>
            </section>

            {/* Date-range override */}
            <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Apply a mode to a date range
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] text-gray-500">Mode</span>
                  <select
                    value={ovMode}
                    onChange={(e) => setOvMode(e.target.value)}
                    className={selectCls}
                  >
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] text-gray-500">From</span>
                  <input
                    type="date"
                    value={ovFrom}
                    onChange={(e) => setOvFrom(e.target.value)}
                    className={dateCls}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] text-gray-500">To (optional)</span>
                  <input
                    type="date"
                    value={ovTo}
                    onChange={(e) => setOvTo(e.target.value)}
                    className={dateCls}
                  />
                </label>
                <button
                  onClick={addOverride}
                  disabled={addingOv}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
                >
                  {addingOv ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Plus className="w-3.5 h-3.5" />
                  )}
                  Add
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">
                Leave "To" empty for an open-ended range. Ranges override the
                default only for the days they cover.
              </p>
            </section>

            {/* Existing overrides */}
            <section>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Active date-range overrides
              </p>
              {ranges.length === 0 ? (
                <p className="text-xs text-gray-400">None — only the default applies.</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {ranges.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-gray-800">
                          {workModeLabel(r.mode)}
                        </span>
                        <span className="text-[11px] text-gray-500 ml-2">
                          {r.from_date} → {r.to_date || "ongoing"}
                        </span>
                      </div>
                      <button
                        onClick={() => removeRange(r.id)}
                        className="text-gray-400 hover:text-red-600 p-1 rounded"
                        title="Remove range"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
